import React, { useState, useEffect } from 'react';
import { customFetch } from '../apiClient';
import Swal from 'sweetalert2';
import { API_BASE } from '../config'; // ✅ ZARURI IMPORT

const HospitalProfile = () => {
    const [initialLoading, setInitialLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    
    const [formData, setFormData] = useState({
        company_Name: '', reg_No: '', prefix: '', slogan1: '', slogan2: '',
        address: '', landmark: '', state: '', district: '', pincode: '',
        contact1: '', contact2: '', email_ID: '', website: '',
        isAyushmanActive: false
    });

    const [logoFile, setLogoFile] = useState(null);
    const [ayushmanLogoFile, setAyushmanLogoFile] = useState(null);
    const [previews, setPreviews] = useState({ logo: '', ayushman: '' });

    // ✅ BULLETPROOF LOGO URL GENERATOR (Same as Receipt.js)
    const getImageUrl = (path) => {
        if (!path) return '';
        const base = API_BASE.replace(/\/api\/?$/, ''); 
        const cleanPath = path.replace(/^\//, ''); 
        return `${base}/${cleanPath}`;
    };

    // 1. Load existing data
    useEffect(() => {
        const loadData = async () => {
            try {
                const hospitalId = sessionStorage.getItem("hospitalId");
                const res = await customFetch(`/Company/${hospitalId}`);
                if (res.ok) {
                    const data = await res.json();
                    setFormData({ ...data });
                    
                    // ✅ FIXED: Using getImageUrl instead of hardcoded localhost
                    setPreviews({
                        logo: data.logo ? getImageUrl(data.logo) : '',
                        ayushman: data.ayushmanLogo ? getImageUrl(data.ayushmanLogo) : ''
                    });
                }
            } catch (err) { 
                console.error("Failed to load profile", err); 
            } finally {
                setInitialLoading(false);
            }
        };
        loadData();
    }, []);

    const handleTextChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            if (type === 'main') {
                setLogoFile(file);
                setPreviews(p => ({ ...p, logo: URL.createObjectURL(file) }));
            } else {
                setAyushmanLogoFile(file);
                setPreviews(p => ({ ...p, ayushman: URL.createObjectURL(file) }));
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const fd = new FormData();
        Object.keys(formData).forEach(key => {
            if (formData[key] !== null) fd.append(key, formData[key]);
        });

        if (logoFile) fd.append('LogoFile', logoFile);
        if (ayushmanLogoFile) fd.append('AyushmanLogoFile', ayushmanLogoFile);

        try {
            const res = await customFetch('/Company/update-profile', {
                method: 'PUT',
                body: fd 
            });

            if (res.ok) {
                const result = await res.json(); // ✅ API Response read kiya taaki nayi image aa jaye
                
                Swal.fire({
                    title: 'Success!',
                    text: 'Hospital Profile Updated Successfully',
                    icon: 'success',
                    confirmButtonColor: 'var(--theme-color)'
                });

                if (formData.company_Name) sessionStorage.setItem("hospitalName", formData.company_Name);
                
                // ✅ FIXED: Naya logo preview mein update karna (taki refresh na karna pade)
                if (result.logoUrl) {
                    setPreviews(p => ({ ...p, logo: getImageUrl(result.logoUrl) }));
                }
                if (result.ayushmanLogoUrl) {
                    setPreviews(p => ({ ...p, ayushman: getImageUrl(result.ayushmanLogoUrl) }));
                }

                // File inputs reset karo
                setLogoFile(null);
                setAyushmanLogoFile(null);

            } else {
                throw new Error(await res.text());
            }
        } catch (err) {
            Swal.fire('Error', err.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) {
        return (
            <div className="profile-page-container d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}></div>
                <span className="ms-3 text-muted fw-bold">Loading Profile Data...</span>
            </div>
        );
    }

    return (
        <div className="profile-page-container">
            <div className="page-header">
                <h2><i className="bi bi-building me-2"></i> Hospital Configuration</h2>
                <p>Manage your hospital's identity, contact details, and system preferences.</p>
            </div>

            <form onSubmit={handleSubmit} className="profile-form">
                
                {/* --- Section 1: Branding & Logos --- */}
                <div className="profile-card branding-section">
                    <h5 className="section-title"><i className="bi bi-award text-warning"></i> Brand Identity</h5>
                    <div className="logo-upload-grid">
                        <div className="logo-box">
                            <label>Hospital Main Logo</label>
                            <div className="preview-circle">
                                {previews.logo ? <img src={previews.logo} alt="Logo" /> : <i className="bi bi-image"></i>}
                                <div className="overlay-hover">
                                    <label htmlFor="mainLogo"><i className="bi bi-camera-fill"></i> Change</label>
                                </div>
                                <input type="file" onChange={(e) => handleFileChange(e, 'main')} accept="image/*" id="mainLogo" hidden />
                            </div>
                        </div>
                        <div className="logo-box">
                            <label>Ayushman Bharat Logo</label>
                            <div className="preview-circle ayushman">
                                {previews.ayushman ? <img src={previews.ayushman} alt="Ayushman" /> : <i className="bi bi-shield-plus"></i>}
                                <div className="overlay-hover">
                                    <label htmlFor="ayushLogo"><i className="bi bi-camera-fill"></i> Change</label>
                                </div>
                                <input type="file" onChange={(e) => handleFileChange(e, 'ayushman')} accept="image/*" id="ayushLogo" hidden />
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- Section 2: Basic Info --- */}
                <div className="profile-card">
                    <h5 className="section-title"><i className="bi bi-info-circle text-primary"></i> General Information</h5>
                    <div className="form-grid">
                        <div className="input-group">
                            <label>Hospital Name <span className="text-danger">*</span></label>
                            <input name="company_Name" value={formData.company_Name || ''} onChange={handleTextChange} required className="hp-input" placeholder="Enter Hospital Name" />
                        </div>
                        <div className="input-group">
                            <label>Registration Number</label>
                            <input name="reg_No" value={formData.reg_No || ''} onChange={handleTextChange} className="hp-input" placeholder="e.g. REG-12345" />
                        </div>
                        <div className="input-group">
                            <label>Bill Prefix</label>
                            <input name="prefix" value={formData.prefix || ''} onChange={handleTextChange} className="hp-input" placeholder="e.g. BILL-" />
                        </div>
                    </div>
                    <div className="form-grid mt-4">
                        <div className="input-group">
                            <label>Slogan 1</label>
                            <input name="slogan1" value={formData.slogan1 || ''} onChange={handleTextChange} className="hp-input" placeholder="Tagline line 1" />
                        </div>
                        <div className="input-group">
                            <label>Slogan 2</label>
                            <input name="slogan2" value={formData.slogan2 || ''} onChange={handleTextChange} className="hp-input" placeholder="Tagline line 2" />
                        </div>
                    </div>
                </div>

                {/* --- Section 3: Contact & Address --- */}
                <div className="profile-card">
                    <h5 className="section-title"><i className="bi bi-geo-alt text-success"></i> Contact & Location</h5>
                    <div className="form-grid">
                        <div className="input-group">
                            <label>Email Address</label>
                            <input name="email_ID" type="email" value={formData.email_ID || ''} onChange={handleTextChange} className="hp-input" placeholder="hospital@example.com" />
                        </div>
                        <div className="input-group">
                            <label>Website</label>
                            <input name="website" value={formData.website || ''} onChange={handleTextChange} className="hp-input" placeholder="www.hospital.com" />
                        </div>
                    </div>
                    <div className="form-grid mt-4">
                        <div className="input-group"><label>Contact Number 1</label><input name="contact1" value={formData.contact1 || ''} onChange={handleTextChange} className="hp-input" placeholder="+91 XXXXX XXXXX" /></div>
                        <div className="input-group"><label>Contact Number 2</label><input name="contact2" value={formData.contact2 || ''} onChange={handleTextChange} className="hp-input" placeholder="+91 XXXXX XXXXX" /></div>
                    </div>
                    <div className="form-grid mt-4">
                        <div className="input-group full-width">
                            <label>Full Address</label>
                            <textarea name="address" value={formData.address || ''} onChange={handleTextChange} className="hp-input" rows="2" placeholder="Street, Locality, Area" />
                        </div>
                    </div>
                    <div className="form-grid mt-4">
                        <div className="input-group"><label>State</label><input name="state" value={formData.state || ''} onChange={handleTextChange} className="hp-input" placeholder="State" /></div>
                        <div className="input-group"><label>District</label><input name="district" value={formData.district || ''} onChange={handleTextChange} className="hp-input" placeholder="District" /></div>
                        <div className="input-group"><label>Pincode</label><input name="pincode" value={formData.pincode || ''} onChange={handleTextChange} className="hp-input" placeholder="PIN Code" /></div>
                    </div>
                </div>

                {/* --- Section 4: System Features --- */}
                <div className="profile-card">
                    <h5 className="section-title"><i className="bi bi-gear text-secondary"></i> System Features</h5>
                    <div className="feature-row">
                        <div className="feature-details">
                            <h6>Ayushman Bharat Integration</h6>
                            <p>Enable to allow Ayushman card processing and specific billing formats.</p>
                        </div>
                        <div className="feature-toggle">
                            <span className={formData.isAyushmanActive ? "text-success fw-bold me-3" : "text-muted me-3"}>
                                {formData.isAyushmanActive ? "Enabled" : "Disabled"}
                            </span>
                            <label className="custom-switch">
                                <input type="checkbox" name="isAyushmanActive" checked={formData.isAyushmanActive} onChange={handleTextChange} />
                                <span className="slider round"></span>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="sticky-footer">
                    <button type="submit" className="save-btn" disabled={loading}>
                        {loading ? (
                            <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> Saving...</>
                        ) : (
                            <><i className="bi bi-shield-check me-2"></i> Save Configuration</>
                        )}
                    </button>
                </div>
            </form>

            <style jsx>{`
                .profile-page-container { max-width: 1000px; margin: 0 auto; padding: 20px 20px 100px 20px; }
                
                .page-header { margin-bottom: 25px; }
                .page-header h2 { font-size: 24px; font-weight: 700; color: #1e293b; margin-bottom: 5px; }
                .page-header p { color: #64748b; font-size: 14px; margin: 0; }

                .profile-card { background: white; border-radius: 12px; padding: 25px; margin-bottom: 25px; box-shadow: 0 2px 12px rgba(0,0,0,0.04); border: 1px solid #e2e8f0; }
                .section-title { font-size: 15px; font-weight: 700; color: #334155; margin-bottom: 20px; display: flex; align-items: center; gap: 8px; border-bottom: 1px solid #f1f5f9; padding-bottom: 12px; }
                
                /* Image Upload UX */
                .logo-upload-grid { display: flex; gap: 50px; justify-content: center; padding: 10px 0; }
                .logo-box { text-align: center; }
                .logo-box label { display: block; font-size: 13px; font-weight: 600; margin-bottom: 12px; color: #475569; }
                
                .preview-circle { width: 120px; height: 120px; border-radius: 50%; background: #f8fafc; border: 2px dashed #cbd5e1; display: flex; align-items: center; justify-content: center; position: relative; font-size: 40px; color: #94a3b8; overflow: hidden; transition: 0.3s; }
                .preview-circle img { width: 100%; height: 100%; object-fit: cover; }
                
                .overlay-hover { position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; opacity: 0; transition: 0.3s; }
                .preview-circle:hover .overlay-hover { opacity: 1; }
                .overlay-hover label { color: white; cursor: pointer; font-size: 12px; font-weight: 600; margin: 0; display: flex; flex-direction: column; align-items: center; gap: 5px; }
                .overlay-hover label i { font-size: 20px; }

                /* Forms */
                .form-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
                .full-width { grid-column: span 3; }
                .input-group { display: flex; flex-direction: column; gap: 6px; }
                .input-group label { font-size: 12px; font-weight: 600; color: #475569; }
                
                .hp-input { width: 100%; padding: 10px 14px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 13px; color: #1e293b; background: #f8fafc; transition: all 0.3s ease; }
                .hp-input:focus { background: white; border-color: var(--theme-color); box-shadow: 0 0 0 3px color-mix(in srgb, var(--theme-color), transparent 85%); outline: none; }
                .hp-input::placeholder { color: #94a3b8; }

                /* Feature Toggle Row */
                .feature-row { display: flex; justify-content: space-between; align-items: center; padding: 15px; background: #f8fafc; border-radius: 8px; border: 1px solid #e2e8f0; }
                .feature-details h6 { font-size: 14px; font-weight: 700; color: #1e293b; margin: 0 0 4px 0; }
                .feature-details p { font-size: 12px; color: #64748b; margin: 0; }
                .feature-toggle { display: flex; align-items: center; }

                /* Custom CSS Switch */
                .custom-switch { position: relative; display: inline-block; width: 44px; height: 24px; }
                .custom-switch input { opacity: 0; width: 0; height: 0; }
                .slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #cbd5e1; transition: .4s; }
                .slider:before { position: absolute; content: ""; height: 18px; width: 18px; left: 3px; bottom: 3px; background-color: white; transition: .4s; }
                input:checked + .slider { background-color: #10b981; }
                input:focus + .slider { box-shadow: 0 0 1px #10b981; }
                input:checked + .slider:before { transform: translateX(20px); }
                .slider.round { border-radius: 24px; }
                .slider.round:before { border-radius: 50%; }

                /* Footer */
                .sticky-footer { position: fixed; bottom: 0; left: 0; right: 0; background: rgba(255,255,255,0.9); backdrop-filter: blur(10px); padding: 15px; border-top: 1px solid #e2e8f0; display: flex; justify-content: center; z-index: 100; box-shadow: 0 -4px 20px rgba(0,0,0,0.05); }
                .save-btn { background: var(--theme-color); color: white; border: none; padding: 12px 40px; border-radius: 8px; font-weight: 600; font-size: 14px; cursor: pointer; display: flex; align-items: center; transition: 0.3s; box-shadow: 0 4px 12px color-mix(in srgb, var(--theme-color), transparent 60%); }
                .save-btn:hover:not(:disabled) { transform: translateY(-2px); filter: brightness(1.1); }
                
                @media (max-width: 768px) { 
                    .form-grid { grid-template-columns: 1fr; gap: 15px; } 
                    .full-width { grid-column: span 1; } 
                    .logo-upload-grid { flex-direction: column; gap: 20px; align-items: center; }
                    .feature-row { flex-direction: column; align-items: flex-start; gap: 15px; }
                }
            `}</style>
        </div>
    );
};

export default HospitalProfile;